import { useState } from "react";
import { Send, Phone, Mail, MapPin, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profession: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({ name: "", email: "", phone: "", profession: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#FAFAF9] noise-bg" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#B45309] mb-4">Get In Touch</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#0F172A] tracking-tight mb-6">
              Let's Discuss Your Next Closing
            </h2>
            <p className="text-lg text-[#64748B] leading-relaxed mb-10">
              Whether you're a realtor with a complex deal, a loan officer with time-sensitive 
              closings, or an attorney seeking a sophisticated title partner, we're ready to help.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0F172A] text-white">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-1">Phone</p>
                  <a href="tel:8003057119" className="font-serif text-lg text-[#0F172A] hover:text-[#B45309] btn-transition">(800) 305-7119</a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0F172A] text-white">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-1">Email</p>
                  <a href="mailto:info@alegaltitle.com" className="font-serif text-lg text-[#0F172A] hover:text-[#B45309] btn-transition">info@alegaltitle.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0F172A] text-white">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-1">Office</p>
                  <p className="font-serif text-lg text-[#0F172A]">
                    2337 Philmont Avenue, Suite 205<br />
                    Huntingdon Valley, PA 19006
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-locations">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0F172A] text-white">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#64748B] mb-1">Service Areas</p>
                  <p className="font-serif text-lg text-[#0F172A]">Pennsylvania • New Jersey • New York • Florida</p>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="flex items-center gap-3 p-4 bg-white border border-[#E7E5E4]">
              <CheckCircle className="w-5 h-5 text-[#B45309]" strokeWidth={1.5} />
              <span className="text-sm text-[#64748B]">
                <strong className="text-[#0F172A]">Same-day response</strong> - we'll be in touch within 24 hours
              </span>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-8 lg:p-10 border border-[#E7E5E4] card-shadow">
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#0F172A] text-white mb-6">
                  <CheckCircle className="w-8 h-8" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl font-medium text-[#0F172A] mb-3">Thank You</h3>
                <p className="text-[#64748B]">We've received your message and will be in touch within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h3 className="font-serif text-2xl font-medium text-[#0F172A] mb-6">Send Us a Message</h3>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] placeholder:text-[#94a3b8]"
                      placeholder="John Smith"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] placeholder:text-[#94a3b8]"
                        placeholder="john@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] placeholder:text-[#94a3b8]"
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      I am a...
                    </label>
                    <select
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A]"
                      data-testid="contact-profession-select"
                    >
                      <option value="">Select your profession</option>
                      <option value="realtor">Real Estate Agent</option>
                      <option value="loan_officer">Loan Officer</option>
                      <option value="attorney">Attorney</option>
                      <option value="homeowner">Homeowner</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[#64748B] mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-0 py-3 border-0 border-b border-[#E7E5E4] bg-transparent text-[#0F172A] focus:border-[#0F172A] resize-none placeholder:text-[#94a3b8]"
                      placeholder="Tell us about your needs..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-3 bg-[#0F172A] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#1e293b] btn-transition disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4" strokeWidth={1.5} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
